import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Box,
  Button,
  COLORS,
  Dialog,
  DialogPopup,
  DialogTitle,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
  Text,
  type Color,
  type IconName,
  toColor,
} from "@salestrak/ui";
import React, { useState } from "react";

import type { ObjectAccessLevel, ObjectVisibility } from "#app/lib/data";

import { RoleSelector } from "#app/components/entity-selectors/role-selector";
import { useRolePool } from "#app/components/entity-selectors/hooks/use-role-pool";
import { TeamSelector } from "#app/components/entity-selectors/team-selector";
import { EntityGroupTag, EntityTag } from "#app/components/entity";
import { userRefToEntity, type EntityData } from "#app/lib/entity-visuals";
import { validateEmail } from "#app/lib/custom-field-utils";
import { useWorkspace } from "#app/lib/data/app-provider";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { useInvitableRoles } from "#app/lib/hooks/use-invitable-roles";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { getDisplayName } from "#app/lib/utils/get-display-name";
import type { Omittable } from "@salestrak/ui";

// Generic presentational share dialog used for both workflows and items.
// The wrapping component (workflow-share-dialog / item-share-dialog) owns
// the GraphQL hook + state and adapts the per-object summary into the
// normalized shape below. One exception: the invite-by-email path lives
// here (member invites are object-agnostic) — the per-object share grant
// still flows through the wrapper's `setMemberShare`.

export type ShareAccessLevel = ObjectAccessLevel;

const ACCESS_LEVEL_OPTIONS: ReadonlyArray<{
  value: ShareAccessLevel;
  label: string;
}> = [
  { value: "full_edit", label: "Full Edit" },
  { value: "edit", label: "Edit" },
  { value: "comment", label: "Comment" },
  { value: "view", label: "View" },
];
// Base UI's `Select items=` renders the selected value's LABEL through
// `SelectValue`, so no call site needs its own value→label lookup.

export type ShareDialogMember = Omittable<{
  id: string;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  profileImage?: string | null;
  accessLevel: ShareAccessLevel;
  isWorkspaceOwner: boolean;
  memberStatus: string;
  enabled: boolean;
  // True when the user's role already grants full edit on this object
  // class (workspace Owner / workflow-admin). Locks the per-row controls.
  hasEditPermission: boolean;
}>;

export type ShareDialogTeam = {
  id: string;
  name: string;
  memberCount: number;
  accessLevel: ShareAccessLevel;
  enabled: boolean;
  icon: string;
  iconColor: string;
};

export type ShareDialogHeaderMember = Omittable<{
  id: string;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  profileImage?: string | null;
  enabled: boolean;
}>;

export type ShareDialogHeaderTeam = {
  id: string;
  name: string;
  icon: string;
  iconColor: string;
  enabled: boolean;
};

export type ShareDialogSummary = {
  visibility: ObjectVisibility;
  teams: ShareDialogTeam[];
  members: ShareDialogMember[];
  headerMembers: ShareDialogHeaderMember[];
  headerTeams: ShareDialogHeaderTeam[];
  memberCount: number;
  enabledMemberCount: number;
  teamCount: number;
  enabledTeamCount: number;
  filteredMemberCount: number;
  filteredTeamCount: number;
};

export type ShareDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  // "Sharing as a single workflow of" / "Sharing as a single project of"
  subheaderText: string;
  subheaderIcon?: { icon?: string | null; color?: string | null } | null;
  subheaderName: string;
  summary: ShareDialogSummary | null;
  loading: boolean;
  hasMoreMembers: boolean;
  loadMoreMembers: () => void;
  hasMoreTeams: boolean;
  loadMoreTeams: () => void;
  setMemberShare: (
    memberId: string,
    accessLevel: ShareAccessLevel,
    enabled: boolean,
  ) => void;
  setTeamShare: (
    teamId: string,
    accessLevel: ShareAccessLevel,
    enabled: boolean,
  ) => void;
  removeAllMembers: () => void;
  removeAllTeams: () => void;
  // "Edit all" — bulk-set every visible (search-matched) row to the
  // picked level. Locked rows (Owner / workflow-editor) are skipped
  // server-side.
  setAllMembers: (accessLevel: ShareAccessLevel) => void;
  setAllTeams: (accessLevel: ShareAccessLevel) => void;
  togglePrivate: () => void;
  // Drives the debounced search box's onValueChange so the wrapping
  // hook re-queries with the active search term.
  onSearchChange: (value: string | undefined) => void;
}>;

export function ShareDialog({
  open,
  onOpenChange,
  title,
  subheaderText,
  subheaderIcon,
  subheaderName,
  summary,
  loading,
  hasMoreMembers,
  loadMoreMembers,
  hasMoreTeams,
  loadMoreTeams,
  setMemberShare,
  setTeamShare,
  removeAllMembers,
  removeAllTeams,
  setAllMembers,
  setAllTeams,
  togglePrivate,
  onSearchChange,
}: ShareDialogProps) {
  const { preferences } = useUserSettings();
  const displayFullNames = preferences?.displayFullNames ?? false;

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 200);
  const activeSearch =
    debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : undefined;

  React.useEffect(() => {
    onSearchChange(activeSearch);
  }, [activeSearch, onSearchChange]);

  // ── Invite-by-email path ──
  // When the search term is a valid email that matches no workspace member
  // (the summary lists ALL members, search-filtered server-side), the
  // access panels swap for an invite form: role + access level + teams,
  // and the footer becomes "Send invite to <email>".
  const { inviteMembers } = useMemberMutations();
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? "";
  const { allowedRoleIds } = useInvitableRoles(workspaceId);
  const { defaultRoleId } = useRolePool({ allowedRoleIds });

  const [inviteRole, setInviteRole] = useState<string | null>(null);
  const [inviteTeamIds, setInviteTeamIds] = useState<string[]>([]);
  const [inviteAccess, setInviteAccess] =
    useState<ShareAccessLevel>("full_edit");
  const [sendingInvite, setSendingInvite] = useState(false);
  const inviteRoleId = inviteRole ?? defaultRoleId;

  const resetInviteState = React.useCallback(() => {
    setInviteRole(null);
    setInviteTeamIds([]);
    setInviteAccess("full_edit");
  }, []);

  // Reset search + invite form when the dialog closes so reopening on
  // another subject starts from a clean state.
  React.useEffect(() => {
    if (!open) {
      setSearchInput("");
      resetInviteState();
    }
  }, [open, resetInviteState]);

  const inviteEmail =
    activeSearch && validateEmail(activeSearch) ? activeSearch : null;
  const inviteMode =
    !!inviteEmail &&
    !loading &&
    summary != null &&
    summary.filteredMemberCount === 0 &&
    summary.filteredTeamCount === 0;

  const handleSendInvite = React.useCallback(async () => {
    if (!inviteEmail || sendingInvite || inviteRoleId === null) return;
    setSendingInvite(true);
    const { succeededUsers } = await inviteMembers([
      { email: inviteEmail, roleId: inviteRoleId, teamIds: inviteTeamIds },
    ]);
    setSendingInvite(false);
    const invited = succeededUsers.find(
      (u) => u.email.toLowerCase() === inviteEmail.toLowerCase(),
    );
    // Failure toasts are shown by the hook — stay on the form for retry.
    if (!invited) return;
    // Grant the new member access to this workflow/item at the chosen
    // level — writes the workflow_share / item_share row via the wrapper.
    setMemberShare(invited.userId, inviteAccess, true);
    // Back to the summary view; the share mutation's refetch lists the
    // new invited-member row.
    setSearchInput("");
    resetInviteState();
  }, [
    inviteEmail,
    sendingInvite,
    inviteRoleId,
    inviteTeamIds,
    inviteAccess,
    inviteMembers,
    setMemberShare,
    resetInviteState,
  ]);

  const isPrivate = summary?.visibility === "private";
  const teams = summary?.teams ?? [];
  const members = summary?.members ?? [];
  const memberCount = summary?.memberCount ?? members.length;
  const enabledMemberCount = summary?.enabledMemberCount ?? memberCount;
  const teamCount = summary?.teamCount ?? teams.length;
  const enabledTeamCount = summary?.enabledTeamCount ?? teamCount;

  const memberHeaderItems: EntityData[] = (summary?.headerMembers ?? []).map(
    (m) => ({
      kind: "user",
      id: m.id,
      user: {
        name: getDisplayName(m, displayFullNames) ?? "",
        profileImage: m.profileImage,
      },
    }),
  );
  const memberHeaderOverflow = Math.max(
    0,
    enabledMemberCount - memberHeaderItems.length,
  );

  const teamHeaderItems: EntityData[] = (summary?.headerTeams ?? []).map(
    (t) => ({
      kind: "team",
      id: t.id,
      team: {
        teamName: t.name,
        icon: { icon: t.icon, color: t.iconColor },
      },
    }),
  );
  const teamHeaderOverflow = Math.max(
    0,
    enabledTeamCount - teamHeaderItems.length,
  );
  const makeScrollHandler =
    (hasMore: boolean, loadMore: () => void) =>
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore) return;
      const target = e.currentTarget;
      const distanceFromBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceFromBottom < 80) loadMore();
    };
  const handleMembersScroll = makeScrollHandler(hasMoreMembers, loadMoreMembers);
  const handleTeamsScroll = makeScrollHandler(hasMoreTeams, loadMoreTeams);

  const SCROLL_HEIGHT = 200;
  const scrollHeightProps = activeSearch
    ? { height: SCROLL_HEIGHT, maxHeight: SCROLL_HEIGHT }
    : { maxHeight: SCROLL_HEIGHT };

  const [openSections, setOpenSections] = useState<string[]>([]);
  const expandAllOnSearchFocus = () => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      next.add("members");
      next.add("teams");
      return Array.from(next);
    });
  };

  const iconName = (subheaderIcon?.icon as IconName | undefined) ?? "circle";
  const iconColor = toColor(subheaderIcon?.color);

  return (
    // The popup carries no padding of its own — that lives in the header,
    // panel and footer parts — and sits on `bg-popover` (DESIGN-TUNABLE).
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup initialFocus={false}>
        {/* minHeight 0 down the column: without it every flex child floors at
            its content height, so when the popup hits its max height the
            content overflows the rounded corners instead of scrolling. */}
        <Box paddingY="md" minHeight={0}>
          <Box gap="md" minHeight={0}>
            <Box gap="2xs" paddingX="md">
              {/* Bare DialogTitle: the a11y part without DialogHeader's own
                  p-6, which would double this Box's gutter and indent the
                  title past the subheader. */}
              <DialogTitle>{title}</DialogTitle>
              <Box direction="row" align="center" gap="2xs">
                <Text size="sm" variant="muted">
                  {subheaderText}
                </Text>
                <Icon name={iconName} size="sm" color={iconColor} />
                <Text size="sm" weight="semibold">
                  {subheaderName}
                </Text>
              </Box>
            </Box>

            <Box paddingX="md">
              {/* Search field: an InputGroup with a leading search glyph,
                  the same recipe as `ExpandableSearch`. */}
              <InputGroup>
                <InputGroupAddon>
                  <Icon name="search" size="sm" color="muted" />
                </InputGroupAddon>
                <InputGroupInput
                  placeholder="Search members or teams"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onFocus={expandAllOnSearchFocus}
                />
              </InputGroup>
            </Box>

            {inviteMode ? (
              <Box paddingX="md" paddingBottom="sm" gap="md">
                <Box gap="sm">
                  <Text size="sm" weight="medium">
                    Invite as
                  </Text>
                  <Box direction="row" align="center" gap="sm">
                    <Box flex={1} minWidth={0}>
                      <RoleSelector
                        value={inviteRoleId}
                        onValueChange={setInviteRole}
                        allowedRoleIds={allowedRoleIds}
                        detailed
                      />
                    </Box>
                    <Select
                      items={ACCESS_LEVEL_OPTIONS}
                      value={inviteAccess}
                      onValueChange={(next: ShareAccessLevel | null) => {
                        if (!next) return;
                        setInviteAccess(next);
                      }}
                    >
                      <SelectTrigger size="sm" width="auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectPopup>
                        {ACCESS_LEVEL_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectPopup>
                    </Select>
                  </Box>
                </Box>
                <Box gap="xs">
                  <Text size="sm" weight="medium">
                    Add to team
                  </Text>
                  <TeamSelector
                    value={inviteTeamIds}
                    onValueChange={setInviteTeamIds}
                    placeholder="Search teams..."
                    icon="users"
                    summary
                    fill
                  />
                </Box>
              </Box>
            ) : (
              <Box paddingX="md">
                <Text size="sm" variant="muted">
                  Access
                </Text>
              </Box>
            )}

            {/* The access sections are the one region that scrolls when the
                dialog can no longer grow; header, search and footer stay
                pinned. */}
            <Box minHeight={0} overflowY="auto">
            {inviteMode ? null : loading && !summary ? (
              <Box align="center" justify="center" padding="md">
                <Spinner />
              </Box>
            ) : (
              <Accordion
                multiple
                value={openSections}
                onValueChange={setOpenSections}
              >
                <AccordionItem value="teams">
                  <Box marginTop="xs" paddingX="md">
                    <AccordionTrigger>
                      <Box fill="flex" direction="row" align="center" justify="between">
                        <Box direction="row" align="center" gap="sm">
                          <Icon name="users" size="md" color="muted" />
                          <Text size="sm" weight="semibold">
                            Teams
                          </Text>
                        </Box>
                        <Box
                          direction="row"
                          align="center"
                          gap="sm"
                          paddingRight="xs"
                        >
                          {teamHeaderItems.length > 0 ? (
                            <EntityGroupTag
                              entities={teamHeaderItems}
                              variant="stack"
                              max={teamHeaderItems.length}
                              size="sm"
                              title="Teams"
                              interactive={false}
                            />
                          ) : null}
                          {teamHeaderOverflow > 0 ? (
                            <Text size="xs" variant="muted">
                              +{teamHeaderOverflow}
                            </Text>
                          ) : null}
                        </Box>
                      </Box>
                    </AccordionTrigger>
                  </Box>
                  <AccordionPanel>
                    <Box marginTop="xs" background="sunken" overflow="hidden">
                      <Box
                        direction="row"
                        align="center"
                        justify="between"
                        paddingX="md"
                        paddingY="xs"
                      >
                        <Text size="sm" variant="muted" weight="medium">
                          {enabledTeamCount === 1
                            ? "1 Team"
                            : `${enabledTeamCount} Teams`}
                        </Text>
                        <Box direction="row" align="center" gap="sm">
                          <EditAllMenu onPick={setAllTeams} />
                          <Box
                            as="button"
                            onClick={removeAllTeams}
                            paddingX="sm"
                            paddingY="2xs"
                            rounded="sm"
                            hoverBackground
                            cursor="pointer"
                          >
                            <Text size="xs" variant="destructive">
                              Remove all
                            </Text>
                          </Box>
                        </Box>
                      </Box>

                      {teams.length === 0 && !activeSearch ? (
                        <Box paddingY="xs" paddingX="md">
                          <Text size="xs" variant="muted">
                            No team shares yet.
                          </Text>
                        </Box>
                      ) : (
                        <Box
                          overflowY="auto"
                          overflowX="hidden"
                          {...scrollHeightProps}
                          onScroll={handleTeamsScroll}
                        >
                          {teams.length === 0 ? (
                            <Box paddingY="xs" paddingX="md">
                              <Text size="xs" variant="muted">
                                No matching teams.
                              </Text>
                            </Box>
                          ) : null}
                          <Box gap="2xs">
                            {teams.map((team, i) => (
                              <Box
                                key={`${team.id}-${i}`}
                                direction="row"
                                align="center"
                                gap="sm"
                                paddingX="md"
                                paddingY="xs"
                              >
                                <Icon
                                  name={(team.icon as IconName) ?? "users"}
                                  size="md"
                                  color={toColor(team.iconColor) ?? "muted"}
                                />
                                <Box
                                  flex={1}
                                  minWidth={0}
                                  direction="row"
                                  align="center"
                                  gap="sm"
                                >
                                  <Text size="sm" truncate={1}>
                                    {team.name}
                                  </Text>
                                  <Text size="xs" variant="muted">
                                    {team.memberCount === 1
                                      ? "1 member"
                                      : `${team.memberCount} members`}
                                  </Text>
                                </Box>
                                <Select
                                  items={ACCESS_LEVEL_OPTIONS}
                                  value={team.accessLevel}
                                  onValueChange={(next: ShareAccessLevel | null) => {
                                    if (!next) return;
                                    setTeamShare(team.id, next, team.enabled);
                                  }}
                                >
                                  <SelectTrigger size="sm" width="auto">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectPopup>
                                    {ACCESS_LEVEL_OPTIONS.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectPopup>
                                </Select>
                                <Switch
                                  checked={team.enabled}
                                  onCheckedChange={(next) =>
                                    setTeamShare(team.id, team.accessLevel, next)
                                  }
                                />
                              </Box>
                            ))}
                            {hasMoreTeams ? (
                              <Box align="center" justify="center" paddingY="xs">
                                <Spinner size="sm" />
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem value="members">
                  <Box marginTop="xs" paddingX="md">
                    <AccordionTrigger>
                      <Box fill="flex" direction="row" align="center" justify="between">
                        <Box direction="row" align="center" gap="sm">
                          <Icon name="user" size="md" color="muted" />
                          <Text size="sm" weight="semibold">
                            Members
                          </Text>
                        </Box>
                        <Box
                          direction="row"
                          align="center"
                          gap="sm"
                          paddingRight="xs"
                        >
                          {memberHeaderItems.length > 0 ? (
                            <EntityGroupTag
                              entities={memberHeaderItems}
                              variant="stack"
                              max={memberHeaderItems.length}
                              size="sm"
                              title="Members"
                              interactive={false}
                            />
                          ) : null}
                          {memberHeaderOverflow > 0 ? (
                            <Text size="xs" variant="muted">
                              +{memberHeaderOverflow}
                            </Text>
                          ) : null}
                        </Box>
                      </Box>
                    </AccordionTrigger>
                  </Box>
                  <AccordionPanel>
                    <Box marginTop="xs" background="sunken" overflow="hidden">
                      <Box
                        direction="row"
                        align="center"
                        justify="between"
                        paddingX="md"
                        paddingY="xs"
                      >
                        <Text size="sm" variant="muted" weight="medium">
                          {enabledMemberCount === 1
                            ? "1 Person"
                            : `${enabledMemberCount} People`}
                        </Text>
                        <Box direction="row" align="center" gap="sm">
                          <EditAllMenu onPick={setAllMembers} />
                          <Box
                            as="button"
                            onClick={removeAllMembers}
                            paddingX="sm"
                            paddingY="2xs"
                            rounded="sm"
                            hoverBackground
                            cursor="pointer"
                          >
                            <Text size="xs" variant="destructive">
                              Remove all
                            </Text>
                          </Box>
                        </Box>
                      </Box>

                      {members.length === 0 && !activeSearch ? (
                        <Box paddingY="xs" paddingX="md">
                          <Text size="xs" variant="muted">
                            No member shares yet.
                          </Text>
                        </Box>
                      ) : (
                        <Box
                          overflowY="auto"
                          overflowX="hidden"
                          {...scrollHeightProps}
                          onScroll={handleMembersScroll}
                        >
                          {members.length === 0 ? (
                            <Box paddingY="xs" paddingX="md">
                              <Text size="xs" variant="muted">
                                No matching members.
                              </Text>
                            </Box>
                          ) : null}
                          <Box gap="2xs">
                            {members.map((member, i) => {
                              const isOwner = member.isWorkspaceOwner;
                              const locked = isOwner || member.hasEditPermission;
                              return (
                                <Box
                                  key={`${member.id}-${i}`}
                                  direction="row"
                                  align="center"
                                  gap="sm"
                                  paddingX="md"
                                  paddingY="xs"
                                >
                                  <EntityTag
                                    entity={userRefToEntity({
                                      userId: member.id,
                                      name: member.name,
                                      username: member.username,
                                      email: member.email,
                                      profileImage: member.profileImage,
                                      memberStatus: member.memberStatus,
                                    })}
                                    display="visual"
                                    size="sm"
                                    interactive={false}
                                  />
                                  <Box
                                    flex={1}
                                    minWidth={0}
                                    direction="row"
                                    align="center"
                                    gap="sm"
                                  >
                                    <Text size="sm" truncate={1}>
                                      {getDisplayName(member, displayFullNames) ??
                                        "—"}
                                    </Text>
                                    {isOwner ? (
                                      <Box
                                        paddingX="xs"
                                        paddingY="2xs"
                                        rounded="sm"
                                        background="muted"
                                      >
                                        <Text size="2xs" variant="muted">
                                          Workspace Owner
                                        </Text>
                                      </Box>
                                    ) : null}
                                  </Box>
                                  <Select
                                    items={ACCESS_LEVEL_OPTIONS}
                                    value={member.accessLevel}
                                    onValueChange={(next: ShareAccessLevel | null) => {
                                      if (!next) return;
                                      setMemberShare(
                                        member.id,
                                        next,
                                        member.enabled,
                                      );
                                    }}
                                    disabled={locked}
                                  >
                                    <SelectTrigger size="sm" width="auto">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectPopup>
                                      {ACCESS_LEVEL_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectPopup>
                                  </Select>
                                  <Switch
                                    checked={member.enabled}
                                    disabled={locked}
                                    onCheckedChange={(next) =>
                                      setMemberShare(
                                        member.id,
                                        member.accessLevel,
                                        next,
                                      )
                                    }
                                  />
                                </Box>
                              );
                            })}
                            {hasMoreMembers ? (
                              <Box align="center" justify="center" paddingY="xs">
                                <Spinner size="sm" />
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}
            </Box>
          </Box>
          <Box paddingX="md" paddingTop="sm">
            {inviteMode ? (
              <Button
                className="w-full"
                onClick={handleSendInvite}
                disabled={sendingInvite || inviteRoleId === null}
                loading={sendingInvite}
              >
                <Icon name="mail" size="sm" />
                <Text>Send invite to {inviteEmail}</Text>
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={togglePrivate}
                disabled={!summary}
              >
                <Icon name={isPrivate ? "users" : "lock"} size="sm" />
                <Text>{isPrivate ? "Make Public" : "Make Private"}</Text>
              </Button>
            )}
          </Box>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}

// "Edit all" action dropdown for the panel headers. Picking a level
// bulk-sets every visible (search-matched) row in that panel
// server-side. It's a Menu (action dropdown), NOT a Select — the
// trigger label stays "Edit all" and no option ever renders as
// selected; the outline-button trigger matches the per-row select
// chrome (border, rounding, height).
function EditAllMenu({
  onPick,
}: {
  onPick: (accessLevel: ShareAccessLevel) => void;
}) {
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button variant="outline" size="sm">
            <Text size="sm">Edit all</Text>
            <Icon name="chevron-down" size="sm" color="muted" />
          </Button>
        }
      />
      <MenuPopup align="end" className="min-w-35">
        {ACCESS_LEVEL_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} onClick={() => onPick(opt.value)}>
            <Text size="sm">{opt.label}</Text>
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}
